import { useCallback, useState } from "react"
import { Upload, X, FileAudio, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  uploadFile,
  validateFile,
  ALLOWED_TYPES,
  type UploadedFile,
} from "@/services"

type UploadState = "idle" | "uploading" | "success" | "error"

type UploadDropzoneProps = {
  onUploadComplete: (file: UploadedFile) => void
  onUploadError?: (error: string) => void
}

export function UploadDropzone({
  onUploadComplete,
  onUploadError,
}: UploadDropzoneProps) {
  const [state, setState] = useState<UploadState>("idle")
  const [progress, setProgress] = useState(0)
  const [dragActive, setDragActive] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleFile = useCallback(async (file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      onUploadError?.(validationError)
      return
    }

    setSelectedFile(file)
    setError(null)
    setState("uploading")
    setProgress(0)

    try {
      const uploaded = await uploadFile(file, setProgress)
      setState("success")
      onUploadComplete(uploaded)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed"
      setState("error")
      setError(message)
      onUploadError?.(message)
    }
  }, [onUploadComplete, onUploadError])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)

    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }, [handleFile])

  const reset = () => {
    setState("idle")
    setProgress(0)
    setSelectedFile(null)
    setError(null)
  }

  return (
    <div
      className={cn(
        "relative border-2 border-dashed rounded-md p-8 transition-colors static-bg glow-border",
        dragActive && "border-primary bg-primary/5",
        state === "error" && "border-destructive",
        state === "success" && "border-green-500",
        state === "idle" && "border-border hover:border-primary/50"
      )}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
      onDragLeave={() => setDragActive(false)}
      onDrop={handleDrop}
    >
      {state === "idle" && (
        <label className="flex flex-col items-center gap-4 cursor-pointer">
          <Upload className="h-10 w-10 text-muted-foreground" />
          <div className="text-center">
            <p className="text-sm font-medium">Drop your audio file here</p>
            <p className="text-xs text-muted-foreground mt-1">
              WAV, MP3, or FLAC up to 50MB
            </p>
          </div>
          <input
            type="file"
            className="sr-only"
            accept={ALLOWED_TYPES.join(",")}
            onChange={handleChange}
          />
        </label>
      )}

      {state === "uploading" && selectedFile && (
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-10 w-10 text-primary animate-spin" />
          <div className="text-center">
            <p className="text-sm font-medium font-mono">{selectedFile.name}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Uploading... {progress}%
            </p>
          </div>
          <div className="w-full max-w-xs h-2 bg-secondary rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {state === "success" && selectedFile && (
        <div className="flex flex-col items-center gap-4">
          <FileAudio className="h-10 w-10 text-green-500" />
          <div className="text-center">
            <p className="text-sm font-medium font-mono">{selectedFile.name}</p>
            <p className="text-xs text-muted-foreground mt-1">
              Upload complete
            </p>
          </div>
        </div>
      )}

      {state === "error" && (
        <div className="flex flex-col items-center gap-4">
          <X className="h-10 w-10 text-destructive" />
          <div className="text-center">
            <p className="text-sm font-medium text-destructive">{error}</p>
          </div>
          <Button variant="secondary" size="sm" onClick={reset}>
            Try again
          </Button>
        </div>
      )}
    </div>
  )
}
